import styled from '@emotion/styled';
import React from 'react';
import { mergeProps, useFocusRing } from 'react-aria';

import { su } from '../../../../theme/functions/su';
import { glyph } from '../../../../theme/glyph';
import { motion } from '../../../../theme/motion';
import { theme } from '../../../../theme/theme';

import { Glyph } from '../../../../components/common/Glyph';
import { HomeyIcon } from '../../../../components/common/HomeyIcon';
import { useOptionContext } from '../../../../components/list-box/ListBox';

export function ColumnItemDefault(props) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars -- intentionally unused
  const { ref, option, state, item, value } = useOptionContext();
  const focusRing = useFocusRing();

  const url = props.url ?? props.iconUrl;
  const backgroundColor = props.backgroundColor ?? props.iconBackgroundColor;

  function getIcon() {
    switch (true) {
      case url != null:
        return (
          <Glyph
            source={url}
            color={theme.color.white}
            size={props.iconSize ?? theme.icon.size_18}
          />
        );
      case props.iconObj != null:
        return (
          <HomeyIcon
            iconObj={props.iconObj}
            color={theme.color.white}
            size={props.iconSize ?? theme.icon.size_small}
          />
        );
      default:
        return null;
    }
  }

  return (
    <ColumnItemDefault.Root
      {...mergeProps(option.optionProps, focusRing.focusProps)}
      ref={ref}
      className={props.className}
      style={props.style}
      as={props.as}
      data-is-selected={option.isSelected}
      data-is-focused={option.isFocused}
      data-is-focus-visible={focusRing.isFocusVisible}
      data-is-pressed={option.isPressed}
      data-is-disabled={option.isDisabled}
    >
      <ColumnItemDefault.IconWrapper backgroundColor={backgroundColor}>
        {getIcon()}
      </ColumnItemDefault.IconWrapper>

      <ColumnItemDefault.Name>{item.textValue}</ColumnItemDefault.Name>

      {item.value.action == null && (
        <ColumnItemDefault.ChevronIcon
          source={glyph.core.chevronRightBold}
          color={theme.color.icon_light}
          size={theme.icon.size_default}
          mirrorRTL={true}
        />
      )}
    </ColumnItemDefault.Root>
  );
}

ColumnItemDefault.Name = styled.div`
  flex: 1 1 0;
  margin-inline-start: ${su(1)};
  transition: color ${motion.transition.micro_partial};
`;

ColumnItemDefault.IconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 ${su(4)};
  height: ${su(4)};
  border-radius: 50%;
  background-color: ${(props) => props.backgroundColor};
  transition: background-color ${motion.transition.micro_partial};
`;

ColumnItemDefault.ChevronIcon = styled(Glyph)`
  transition: color ${motion.transition.micro_partial};
`;

ColumnItemDefault.Root = styled.li`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 5px ${su(1)};
  margin: 0 ${su(1)};
  border-radius: ${theme.borderRadius.default};
  outline: 0;
  cursor: pointer;
  transition: background-color ${motion.transition.micro_partial};

  &[data-is-focus-visible='true'],
  &:hover {
    background-color: ${theme.color.background_hover};

    ${ColumnItemDefault.Name} {
      font-weight: ${theme.fontWeight.medium};
    }
  }

  &[data-is-focus-visible='true']:not([aria-selected='true']),
  &:hover:not([data-is-selected='true']) {
    ${ColumnItemDefault.ChevronIcon} {
      color: ${theme.icon.color_dark};
    }
  }

  &[data-is-selected='true'] {
    ${ColumnItemDefault.Name} {
      color: ${theme.color.highlight};
      font-weight: ${theme.fontWeight.medium};
    }

    ${ColumnItemDefault.IconWrapper} {
      background-color: ${theme.color.highlight};
    }

    ${ColumnItemDefault.ChevronIcon} {
      color: ${theme.color.highlight};
    }
  }
`;
